import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Filter, ScrollText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const VarianceReport = () => {
  const navigate = useNavigate();
  const [consignmentId, setConsignmentId] = useState('');
  const [onlyVariance, setOnlyVariance] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [rollup, setRollup] = useState([]);

  const load = async () => {
    const params = {};
    if (consignmentId) params.consignment_id = consignmentId;
    if (onlyVariance) params.only_with_variance = true;
    const r = await apiClient.get('/shirt-mfg/variance-report', { params });
    setBundles(r.data.bundles || []);
    setRollup(r.data.consignment_rollup || []);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const exportCsv = () => {
    const header = [
      'Bundle Code', 'Consignment', 'Design', 'Shade',
      'Declared', 'Actual', 'Variance', 'Status', 'Inspected By', 'Inspected At',
    ];
    const rows = bundles.map((b) => [
      b.bundle_code, b.consignment_id || '', b.design_name || '',
      b.shade || '', b.declared_qty, b.actual_qty ?? '', b.variance ?? '',
      b.status, b.inspected_by || '', b.inspected_at || '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shirt-mfg-variance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-variance-report">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sections/shirt-manufacturing')}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="variance-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold font-display">Variance Report</h1>
            <p className="text-sm text-[#E8DCC8]">
              Bundle-level detail & consignment roll-up
            </p>
          </div>
          <Button
            onClick={exportCsv}
            variant="secondary"
            data-testid="variance-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div>
              <Input
                placeholder="Consignment ID…"
                value={consignmentId}
                onChange={(e) => setConsignmentId(e.target.value)}
                className="w-72"
                data-testid="variance-consignment-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="only-var" checked={onlyVariance}
                onCheckedChange={setOnlyVariance}
                data-testid="variance-only-checkbox"
              />
              <label htmlFor="only-var" className="text-sm">Only with variance</label>
            </div>
            <Button
              size="sm" onClick={load}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="variance-apply-btn"
            >
              Apply
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Consignment Roll-up</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Consignment</TableHead>
                  <TableHead>Bundles</TableHead>
                  <TableHead>Declared</TableHead>
                  <TableHead>Actual</TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>With Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!rollup.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-400 py-6">
                      No inspected bundles to roll up yet
                    </TableCell>
                  </TableRow>
                )}
                {rollup.map((r) => (
                  <TableRow key={r.consignment_id} data-testid={`variance-rollup-${r.consignment_id}`}>
                    <TableCell className="font-mono text-xs">{r.consignment_id}</TableCell>
                    <TableCell>{r.bundle_count}</TableCell>
                    <TableCell>{r.declared_total}</TableCell>
                    <TableCell>{r.actual_total}</TableCell>
                    <TableCell>
                      <Badge variant={r.variance_total === 0 ? 'outline' : 'destructive'}>
                        {r.variance_total >= 0 ? '+' : ''}{r.variance_total}
                      </Badge>
                    </TableCell>
                    <TableCell>{r.bundles_with_variance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Bundle-level Detail ({bundles.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bundle</TableHead>
                  <TableHead>Consignment</TableHead>
                  <TableHead>Design</TableHead>
                  <TableHead>Shade</TableHead>
                  <TableHead>Declared</TableHead>
                  <TableHead>Actual</TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!bundles.length && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-400 py-6">
                      No bundles match the filters
                    </TableCell>
                  </TableRow>
                )}
                {bundles.map((b) => (
                  <TableRow key={b.bundle_code} data-testid={`variance-row-${b.bundle_code}`}>
                    <TableCell className="font-mono text-xs">{b.bundle_code}</TableCell>
                    <TableCell className="text-xs">{b.consignment_id || '—'}</TableCell>
                    <TableCell className="text-xs">{b.design_name}</TableCell>
                    <TableCell className="text-xs">{b.shade}</TableCell>
                    <TableCell>{b.declared_qty}</TableCell>
                    <TableCell>{b.actual_qty ?? '—'}</TableCell>
                    <TableCell>
                      {b.variance === null || b.variance === undefined ? '—' : (
                        <Badge variant={b.variance === 0 ? 'outline' : 'destructive'}>
                          {b.variance >= 0 ? '+' : ''}{b.variance}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{b.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VarianceReport;
